import { useEffect, useRef } from 'react';

// Cloudflare Turnstile widget. Loads the script lazily on mount and renders
// the challenge into a div. The widget calls `callback` with a token; we
// hand that to the parent. On error/expiry we clear the token so the form
// submit re-blocks until the user retries.
//
// Test site key (1x00000000000000000000AA) always passes silently and is
// used in dev. Production key arrives via the /api/v1/config endpoint.
//
// Callbacks are stashed in refs so that an unstable identity in the parent
// (e.g. inline arrow function for onExpired) does NOT remount the widget.
// Each remount issues a fresh Cloudflare challenge; the previous one would
// loop after the first solve, hammering Turnstile and never settling.

declare global {
  interface Window {
    turnstile?: {
      render: (
        target: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

let scriptLoaded: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (scriptLoaded) return scriptLoaded;
  scriptLoaded = new Promise<void>((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile_script_load_failed'));
    document.head.appendChild(s);
  });
  return scriptLoaded;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onExpired,
}: {
  siteKey: string | null;
  onToken: (token: string) => void;
  onExpired?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const onExpiredRef = useRef(onExpired);

  // Keep refs current so the stable handlers below always see the latest props
  // without forcing the mount-effect to re-run on every parent render.
  useEffect(() => {
    onTokenRef.current = onToken;
    onExpiredRef.current = onExpired;
  }, [onToken, onExpired]);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => onTokenRef.current(token),
        'error-callback': () => onExpiredRef.current?.(),
        'expired-callback': () => onExpiredRef.current?.(),
      });
    }).catch(() => {
      // Script failed to load — render the div empty; the form will warn.
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
      }
    };
  }, [siteKey]);

  if (!siteKey) {
    return (
      <p className="inline-flex items-center gap-2 rounded-pill border border-glass-border-strong bg-white/60 backdrop-blur-md px-3.5 py-1.5 font-sans text-caption text-text-secondary">
        <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-pulse" />
        Anti-spam ochrana sa nastavuje. Kontaktný formulár bude o chvíľu k dispozícii.
      </p>
    );
  }
  return <div ref={containerRef} className="inline-block" />;
}
